import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Archive,
  Bell,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  Clock3,
  CreditCard,
  Copy,
  Database,
  ExternalLink,
  Eye,
  Flag,
  HardDrive,
  Image,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Menu,
  MoreHorizontal,
  Plus,
  QrCode,
  Search,
  Settings,
  ShieldCheck,
  Tags,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  archiveEvent,
  archiveEventType,
  clearStoredSession,
  createEvent,
  createEventType,
  createPhotographer,
  deleteEvent,
  deletePhotographer,
  getStoredSession,
  loadAdminDashboard,
  loadAdminHealth,
  loadPartnerDirectory,
  loadStudioMembers,
  logout,
  publishEvent,
  requestAdminOtp,
  reviewPhotographer,
  resolveModeration,
  rotateEventAccess,
  sponsorEventPlan,
  storeSession,
  transitionEvent,
  updateEvent,
  updateEventAssignments,
  updateEventType,
  verifyAdminOtp,
} from "./api";
import { EventManagement as ProfessionalEventManagement } from "./EventManagement";
import { PartnerDirectory } from "./PartnerDirectory";
import { PlanCatalogManagement } from "./PlanCatalogManagement";
import { StudioManagement } from "./StudioManagement";
import { getAdminViewFromUrl, navigateAdminView } from "./routes";
import { captureOtpFill, showDevOtpFill } from "./otp-fill";
import "./dynamic.css";

const nav = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "events", label: "Events & QR", icon: CalendarDays },
  { id: "studios", label: "Studios", icon: Building2 },
  { id: "event-types", label: "Event types", icon: Tags },
  { id: "partners", label: "Photographer partners", icon: Users },
  { id: "partner-directory", label: "Partner directory", icon: Search },
  { id: "moderation", label: "Moderation", icon: Flag },
  { id: "retention", label: "Storage & retention", icon: Database },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
];

export function configuredAdminEmail(environment = process.env) {
  return String(environment.REACT_APP_ADMIN_EMAIL || "")
    .trim()
    .toLowerCase();
}

function App() {
  const [otpFill, setOtpFill] = useState(() => captureOtpFill("admin"));
  const [session, setSession] = useState(() => getStoredSession());
  const [dashboard, setDashboard] = useState(null);
  const [view, setViewState] = useState(() => getAdminViewFromUrl());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(Boolean(session));
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [eventAccess, setEventAccess] = useState(null);
  const [otpRequested, setOtpRequested] = useState(Boolean(otpFill));
  const [partnerBusyId, setPartnerBusyId] = useState("");
  const [eventTypeBusyId, setEventTypeBusyId] = useState("");
  const [devCode, setDevCode] = useState("");
  const [adminEmail, setAdminEmail] = useState(
    () => otpFill?.email || configuredAdminEmail(),
  );

  const setView = useCallback((nextView) => {
    if (!navigateAdminView(nextView)) return;
    setViewState(nextView);
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    function handleOtpHash() {
      const fill = captureOtpFill("admin");
      if (fill) {
        setOtpFill(fill);
        setAdminEmail(fill.email);
        setOtpRequested(true);
      }
    }
    window.addEventListener("hashchange", handleOtpHash);
    window.addEventListener("focus", handleOtpHash);
    return () => {
      window.removeEventListener("hashchange", handleOtpHash);
      window.removeEventListener("focus", handleOtpHash);
    };
  }, []);

  useEffect(() => {
    function handlePopState() {
      setViewState(getAdminViewFromUrl());
    }
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("hashchange", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("hashchange", handlePopState);
    };
  }, []);

  const refresh = useCallback(async (token, signal) => {
    setLoading(true);
    setError("");
    try {
      const nextDashboard = await loadAdminDashboard(token, signal);
      setDashboard(nextDashboard);
      loadAdminHealth(token, signal)
        .then((health) => {
          if (signal?.aborted) return;
          setDashboard((current) =>
            current ? { ...current, health } : current,
          );
        })
        .catch((healthError) => {
          if (signal?.aborted) return;
          setDashboard((current) =>
            current
              ? {
                  ...current,
                  health: {
                    healthy: false,
                    checks: {},
                    error: healthError.code || "HEALTH_CHECK_UNAVAILABLE",
                  },
                }
              : current,
          );
        });
    } catch (requestError) {
      if (requestError.name === "AbortError") return;
      if (requestError.status === 401 || requestError.status === 403) {
        clearStoredSession();
        setSession(null);
        setDashboard(null);
        setError(
          requestError.status === 403
            ? "This account does not have platform administrator access."
            : "Your session expired. Sign in again.",
        );
      } else {
        setDashboard(null);
        setError(
          `Backend unavailable (${requestError.code || "network error"}). No fallback records are shown.`,
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!session) return undefined;
    const controller = new AbortController();
    refresh(session.token, controller.signal);
    return () => controller.abort();
  }, [refresh, session]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(() => setToast(""), 2500);
    return () => clearTimeout(timeout);
  }, [toast]);

  async function handleSendOtp() {
    setLoading(true);
    setError("");
    setDevCode("");
    try {
      const result = await requestAdminOtp(adminEmail.trim());
      setOtpRequested(true);
      if (result?.devCode) {
        setDevCode(result.devCode);
      }
    } catch (requestError) {
      setError(
        requestError.message ||
          "The administrator login code could not be sent.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(code) {
    setLoading(true);
    setError("");
    try {
      const result = await verifyAdminOtp(adminEmail.trim(), code);
      if (!result.user?.roles?.includes("admin")) {
        throw Object.assign(new Error("Admin access is required."), {
          status: 403,
        });
      }
      const nextSession = { user: result.user };
      storeSession({ user: result.user });
      setSession(nextSession);
    } catch (loginError) {
      clearStoredSession();
      setError(loginError.message || "The OTP is invalid or expired.");
      setLoading(false);
    }
  }

  async function handleLogout() {
    clearStoredSession();
    setSession(null);
    setDashboard(null);
    await logout().catch(() => undefined);
  }

  async function handleModeration(item, status) {
    try {
      await resolveModeration(session.token, item.id, status);
      setDashboard((current) => ({
        ...current,
        moderation: current.moderation.filter((entry) => entry.id !== item.id),
        overview: {
          ...current.overview,
          counts: {
            ...current.overview.counts,
            openModeration: Math.max(
              0,
              current.overview.counts.openModeration - 1,
            ),
          },
        },
      }));
      setToast(`Moderation case ${status}`);
      setConfirmDelete(null);
    } catch (requestError) {
      setToast(requestError.message || "Unable to update moderation case");
    }
  }

  async function handlePhotographerReview(profile, status, reason) {
    setPartnerBusyId(profile.id);
    try {
      const updated = await reviewPhotographer(
        session.token,
        profile.id,
        status,
        reason,
      );
      setDashboard((current) => ({
        ...current,
        photographers: (current.photographers || []).map((item) =>
          item.id === updated.id ? updated : item,
        ),
      }));
      setToast(`Photographer application ${status.replace("_", " ")}`);
    } catch (requestError) {
      setToast(
        requestError.message || "Unable to review photographer application",
      );
    } finally {
      setPartnerBusyId("");
    }
  }

  async function handleCreatePhotographer(values) {
    setPartnerBusyId("new");
    try {
      const created = await createPhotographer(session.token, values);
      setDashboard((current) => ({
        ...current,
        photographers: [created, ...(current.photographers || [])],
      }));
      setToast(
        `Photographer ${created.user?.displayName || created.businessName} onboarded and auto-approved`,
      );
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to add photographer");
      return false;
    } finally {
      setPartnerBusyId("");
    }
  }

  async function handleDeletePhotographer(profile) {
    setPartnerBusyId(profile.id);
    try {
      await deletePhotographer(session.token, profile.id);
      setDashboard((current) => ({
        ...current,
        photographers: (current.photographers || []).filter(
          (item) => item.id !== profile.id,
        ),
      }));
      setToast(
        `Photographer ${profile.user?.displayName || profile.businessName} deleted`,
      );
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to delete photographer");
      return false;
    } finally {
      setPartnerBusyId("");
    }
  }

  async function handleCreateEventType(values) {
    setEventTypeBusyId("new");
    try {
      const created = await createEventType(session.token, values);
      setDashboard((current) => ({
        ...current,
        eventTypes: [...(current.eventTypes || []), created].sort(
          eventTypeSort,
        ),
      }));
      setToast(`${created.name} added to the event catalogue`);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to create event type");
      return false;
    } finally {
      setEventTypeBusyId("");
    }
  }

  async function handleUpdateEventType(item, values) {
    setEventTypeBusyId(item.id);
    try {
      const updated = await updateEventType(session.token, item.id, values);
      setDashboard((current) => ({
        ...current,
        eventTypes: (current.eventTypes || [])
          .map((entry) => (entry.id === updated.id ? updated : entry))
          .sort(eventTypeSort),
      }));
      setToast(`${updated.name} updated`);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to update event type");
      return false;
    } finally {
      setEventTypeBusyId("");
    }
  }

  async function handleArchiveEventType(item) {
    setEventTypeBusyId(item.id);
    try {
      const updated = await archiveEventType(session.token, item.id);
      setDashboard((current) => ({
        ...current,
        eventTypes: (current.eventTypes || []).map((entry) =>
          entry.id === updated.id ? updated : entry,
        ),
      }));
      setToast(`${updated.name} archived`);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to archive event type");
      return false;
    } finally {
      setEventTypeBusyId("");
    }
  }

  async function handleCreateEvent(values) {
    try {
      await createEvent(session.token, values);
      setToast("Draft event created. The client can now activate its plan.");
      await refresh(session.token);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to create event");
      return false;
    }
  }

  async function handleUpdateEvent(item, details, assignments) {
    try {
      await updateEvent(session.token, item.id, details);
      if (assignments) {
        await updateEventAssignments(session.token, item.id, assignments);
      }
      setToast("Event changes saved without altering existing access.");
      await refresh(session.token);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to update event");
      return false;
    }
  }

  async function handlePartnerAssignment(
    item,
    assignedPhotographerIds,
    reason,
  ) {
    try {
      await updateEventAssignments(session.token, item.id, {
        assignedPhotographerIds,
        reason,
      });
      setToast(`Partner team updated for ${item.name}`);
      await refresh(session.token);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to update the partner team");
      return false;
    }
  }

  async function handleEventTransition(item, status) {
    try {
      if (status === "archived") await archiveEvent(session.token, item.id);
      else await transitionEvent(session.token, item.id, status);
      setToast(`${item.name} moved to ${status}`);
      await refresh(session.token);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to update event status");
      return false;
    }
  }

  async function handleDeleteEvent(item) {
    try {
      await deleteEvent(session.token, item.id);
      setToast(`${item.name} permanently deleted`);
      await refresh(session.token);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to delete event");
      return false;
    }
  }

  async function handlePublishEvent(item) {
    try {
      await publishEvent(session.token, item.id);
      const access = await rotateEventAccess(session.token, item.id);
      setEventAccess({ ...access, event: item });
      setToast("Event published and QR access activated");
      await refresh(session.token);
    } catch (requestError) {
      setToast(requestError.message || "Unable to publish event");
    }
  }

  async function handleQr(item) {
    try {
      const access = await rotateEventAccess(session.token, item.id);
      setEventAccess({ ...access, event: item });
    } catch (requestError) {
      setToast(requestError.message || "Unable to create QR");
    }
  }

  async function handleSponsorPlan(item) {
    try {
      await sponsorEventPlan(session.token, item.id);
      setToast(`Plan activated for ${item.name}`);
      await refresh(session.token);
      return true;
    } catch (requestError) {
      setToast(requestError.message || "Unable to activate plan");
      return false;
    }
  }

  if (!session)
    return (
      <LoginScreen
        email={adminEmail}
        onEmailChange={setAdminEmail}
        otpRequested={otpRequested}
        initialCode={otpFill?.code || ""}
        fillCode={otpFill?.code || ""}
        sendOtp={handleSendOtp}
        verifyOtp={handleVerifyOtp}
        reset={() => {
          setOtpRequested(false);
          setError("");
          setOtpFill(null);
        }}
        loading={loading}
        error={error}
        devCode={devCode}
      />
    );
  if (loading && !dashboard) return <LoadingScreen />;
  if (!dashboard)
    return (
      <FailureScreen
        message={error}
        retry={() => refresh()}
        signOut={handleLogout}
      />
    );

  const data = dashboard;
  const cases = data.moderation || [];
  const userName =
    session.user?.displayName || session.user?.email || "Administrator";
  const initials = userName
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <main className="admin-app bg-canvas font-sans text-ink selection:bg-teal/20">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <span>
            <ShieldCheck size={19} />
          </span>
          photo<b>x</b>
          <em>admin</em>
        </div>
        <nav aria-label="Administration navigation">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={view === item.id ? "active" : ""}
                onClick={() => setView(item.id)}
              >
                <Icon size={18} />
                <span>{item.label}</span>
                {item.id === "moderation" && cases.length > 0 && (
                  <em>{cases.length}</em>
                )}
              </button>
            );
          })}
        </nav>
        <div className="admin-side-bottom">
          <button>
            <Settings size={18} /> Platform settings
          </button>
          <button onClick={handleLogout}>
            <LogOut size={18} /> Sign out
          </button>
          <div className="admin-profile">
            <span>{initials}</span>
            <div>
              <strong>{userName}</strong>
              <small>Platform administrator</small>
            </div>
            <MoreHorizontal size={17} />
          </div>
        </div>
      </aside>
      <section className="admin-main">
        <header className="admin-topbar">
          <button
            className="mobile-admin-logo"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open mobile navigation"
          >
            <Menu size={20} />
          </button>
          <div className="search">
            <Search size={17} />
            <input
              placeholder="Search platform records…"
              aria-label="Search platform records"
            />
          </div>
          <nav
            className="admin-top-nav"
            aria-label="Top administration navigation"
          >
            {nav.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`admin-top-nav-btn ${view === item.id ? "active" : ""}`}
                  onClick={() => setView(item.id)}
                >
                  <Icon size={14} />
                  <span>{item.label}</span>
                  {item.id === "moderation" && cases.length > 0 && (
                    <em className="top-badge">{cases.length}</em>
                  )}
                </button>
              );
            })}
          </nav>
          <div className="environment">
            <i /> Development <ChevronDown size={14} />
          </div>
          <button className="bell">
            <Bell size={19} />
            <span />
          </button>
        </header>
        <div className="admin-content">
          {error && (
            <div className="admin-alert" role="status">
              <AlertTriangle size={16} /> {error}
            </div>
          )}
          {data.health?.checks?.email?.healthy === false && (
            <div className="admin-alert" role="alert">
              <AlertTriangle size={16} /> Transactional email is unavailable. On
              Render Free, configure AWS SES because outbound SMTP ports are
              blocked.
            </div>
          )}
          {view === "overview" && (
            <Overview
              data={data}
              cases={cases}
              setView={setView}
              refresh={() => refresh(session.token)}
            />
          )}
          {view === "studios" && (
            <StudioManagement
              token={session.token}
              studios={data.studios || []}
              photographers={data.photographers || []}
              refresh={() => refresh(session.token)}
              notify={setToast}
            />
          )}
          {view === "events" && (
            <ProfessionalEventManagement
              events={data.events}
              studios={data.studios}
              subscriptions={data.subscriptions}
              eventTypes={data.eventTypes || []}
              photographers={data.photographers || []}
              plans={data.plans || []}
              token={session.token}
              create={handleCreateEvent}
              update={handleUpdateEvent}
              transition={handleEventTransition}
              remove={handleDeleteEvent}
              publish={handlePublishEvent}
              sponsorPlan={handleSponsorPlan}
              showQr={handleQr}
              setToast={setToast}
            />
          )}
          {view === "partners" && (
            <PhotographerApprovals
              profiles={data.photographers || []}
              review={handlePhotographerReview}
              remove={handleDeletePhotographer}
              busyId={partnerBusyId}
              createPhotographer={handleCreatePhotographer}
              studios={data.studios || []}
              eventTypes={data.eventTypes || []}
            />
          )}
          {view === "partner-directory" && (
            <PartnerDirectory
              token={session.token}
              events={data.events || []}
              eventTypes={data.eventTypes || []}
              loadDirectory={loadPartnerDirectory}
              assign={handlePartnerAssignment}
            />
          )}
          {view === "event-types" && (
            <EventTypeManagement
              eventTypes={data.eventTypes || []}
              create={handleCreateEventType}
              update={handleUpdateEventType}
              archive={handleArchiveEventType}
              busyId={eventTypeBusyId}
            />
          )}
          {view === "moderation" && (
            <Moderation
              cases={cases}
              approve={(item) => handleModeration(item, "approved")}
              remove={setConfirmDelete}
            />
          )}
          {view === "retention" && (
            <Retention
              retention={data.retention}
              overview={data.overview}
              setToast={setToast}
            />
          )}
          {view === "subscriptions" && (
            <Subscriptions
              report={data.subscriptions}
              plans={data.plans || []}
              setToast={setToast}
            />
          )}
        </div>
      </section>
      <nav className="admin-mobile-nav">
        {nav.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              className={view === item.id ? "active" : ""}
              aria-current={view === item.id ? "page" : undefined}
              onClick={() => setView(item.id)}
            >
              <Icon size={20} />
              <span>{item.label.split(" ")[0]}</span>
            </button>
          );
        })}
      </nav>
      {mobileMenuOpen && (
        <div
          className="mobile-drawer-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        >
          <aside
            className="mobile-drawer"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile-drawer-header">
              <div className="admin-brand">
                <span>
                  <ShieldCheck size={19} />
                </span>
                photo<b>x</b>
                <em>admin</em>
              </div>
              <button
                className="mobile-drawer-close"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close navigation"
              >
                <X size={20} />
              </button>
            </div>
            <nav
              className="mobile-drawer-nav"
              aria-label="Mobile drawer navigation"
            >
              {nav.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    className={view === item.id ? "active" : ""}
                    onClick={() => setView(item.id)}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                    {item.id === "moderation" && cases.length > 0 && (
                      <em>{cases.length}</em>
                    )}
                  </button>
                );
              })}
            </nav>
            <div className="admin-side-bottom">
              <button onClick={handleLogout}>
                <LogOut size={18} /> Sign out
              </button>
              <div className="admin-profile">
                <span>{initials}</span>
                <div>
                  <strong>{userName}</strong>
                  <small>Platform administrator</small>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
      {confirmDelete && (
        <Confirm
          cancel={() => setConfirmDelete(null)}
          confirm={() => handleModeration(confirmDelete, "removed")}
        />
      )}
      {eventAccess && (
        <QrModal
          access={eventAccess}
          close={() => setEventAccess(null)}
          setToast={setToast}
        />
      )}
      {toast && (
        <div className="toast">
          <Check size={16} /> {toast}
        </div>
      )}
    </main>
  );
}

function LoginScreen({
  email,
  onEmailChange,
  otpRequested,
  initialCode,
  fillCode,
  sendOtp,
  verifyOtp,
  reset,
  loading,
  error,
  devCode,
}) {
  const [code, setCode] = useState(initialCode || fillCode || "");

  useEffect(() => {
    if (fillCode) {
      setCode(fillCode);
    }
  }, [fillCode]);

  return (
    <main className="admin-login">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (otpRequested) verifyOtp(code);
          else sendOtp();
        }}
      >
        <span className="login-mark">
          <ShieldCheck size={28} />
        </span>
        <p className="eyebrow">PhotoX operations</p>
        <h1>{otpRequested ? "Check admin email" : "Admin sign in"}</h1>
        <p>
          {otpRequested
            ? `Enter the one-time code sent to ${email}.`
            : "A secure one-time code will be sent to the authorized administrator email."}
        </p>
        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}
        {fillCode && otpRequested && (
          <button
            type="button"
            className="dev-otp-badge"
            onClick={() => setCode(fillCode)}
            title="Tap to fill OTP code from magic link"
          >
            <span>Magic link OTP:</span> <strong>{fillCode}</strong>
            <em>Tap to fill</em>
          </button>
        )}
        {showDevOtpFill(devCode) && !fillCode && (
          <button
            type="button"
            className="dev-otp-badge"
            onClick={() => setCode(devCode)}
            title="Tap to fill the same OTP emailed by SES (testing only)"
          >
            <span>Testing OTP:</span> <strong>{devCode}</strong>
            <em>{code === devCode ? "✓ Filled" : "Tap to fill"}</em>
          </button>
        )}
        <label>
          Administrator email
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            readOnly={otpRequested}
            required
            placeholder="admin@yourcompany.com"
          />
        </label>
        {otpRequested && (
          <label>
            6-digit OTP
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength="6"
              value={code}
              onChange={(event) =>
                setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
              }
              required
            />
          </label>
        )}
        <button
          className="button button-dark"
          disabled={loading || (otpRequested && code.length !== 6)}
        >
          {loading ? (
            <LoaderCircle className="spin" size={18} />
          ) : (
            <LockKeyhole size={18} />
          )}{" "}
          {loading
            ? "Please wait…"
            : otpRequested
              ? "Verify & sign in"
              : "Send OTP"}
        </button>
        {otpRequested && (
          <button type="button" className="otp-reset" onClick={reset}>
            Send a new code
          </button>
        )}
      </form>
    </main>
  );
}

function LoadingScreen() {
  return (
    <main className="admin-loading">
      <LoaderCircle className="spin" size={30} />
      <strong>Loading protected administration data…</strong>
    </main>
  );
}
function FailureScreen({ message, retry, signOut }) {
  return (
    <main className="admin-loading">
      <AlertTriangle size={30} />
      <strong>Administration data could not be loaded</strong>
      <p>{message}</p>
      <div>
        <button className="button button-dark" onClick={retry}>
          Retry
        </button>
        <button className="button button-light" onClick={signOut}>
          Sign out
        </button>
      </div>
    </main>
  );
}
function Heading({ eyebrow, title, text, action }) {
  return (
    <div className="admin-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
      {action}
    </div>
  );
}
function Metric({ label, value, change, icon: Icon }) {
  return (
    <article>
      <span>
        <Icon size={19} />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <em>{change}</em>
      </div>
    </article>
  );
}

function Overview({ data, setView, cases, refresh }) {
  const { counts = {}, storageBytes = 0 } = data.overview || {};
  const checks = data.health?.checks || {};
  const healthRows = Object.entries(checks).slice(0, 4);
  return (
    <>
      <Heading
        eyebrow="Platform pulse"
        title="Operations overview"
        text="Live development activity across events, storage and memberships."
        action={
          <button className="updated" onClick={refresh}>
            <Clock3 size={14} /> Refresh
          </button>
        }
      />
      <div className="admin-metrics">
        <Metric
          label="Total events"
          value={formatCount(counts.events)}
          change="Persisted records"
          icon={CalendarDays}
        />
        <Metric
          label="Active studios"
          value={formatCount(counts.studios)}
          change="Persisted records"
          icon={Users}
        />
        <Metric
          label="Stored photos"
          value={formatCount(counts.photos)}
          change={formatBytes(storageBytes)}
          icon={Image}
        />
        <Metric
          label="Subscriptions"
          value={formatCount(counts.subscriptions)}
          change="All statuses"
          icon={CreditCard}
        />
      </div>
      <div className="admin-grid">
        <section className="admin-panel platform-chart">
          <div className="panel-title">
            <div>
              <h2>Platform activity</h2>
              <p>Current persisted record distribution</p>
            </div>
          </div>
          <div className="chart">
            <div className="y-labels">
              <span>100%</span>
              <span>66%</span>
              <span>33%</span>
              <span>0</span>
            </div>
            <div className="bars">
              {[
                ["Users", counts.users],
                ["Studios", counts.studios],
                ["Requests", counts.openInquiries],
                ["Events", counts.events],
                ["Photos", counts.photos],
                ["Plans", counts.subscriptions],
              ].map(([label, value]) => (
                <span key={label}>
                  <i
                    style={{
                      height: `${Math.max(8, Math.min(100, Number(value || 0) * 12))}%`,
                    }}
                  />
                  <small>{label}</small>
                </span>
              ))}
            </div>
          </div>
        </section>
        <section className="admin-panel health">
          <div className="panel-title">
            <div>
              <h2>System health</h2>
              <p>Backend provider readiness</p>
            </div>
            <span
              className={`status ${data.health?.healthy ? "status-live" : "status-review"}`}
            >
              {data.health?.healthy ? "Healthy" : "Attention"}
            </span>
          </div>
          {healthRows.length ? (
            healthRows.map(([name, check]) => (
              <div className="health-row" key={name}>
                <span className={check?.healthy ? "healthy" : "pending"} />
                <strong>{humanize(name)}</strong>
                <small>
                  {check?.healthy
                    ? "Ready"
                    : check?.status || check?.error || "Deferred"}
                </small>
              </div>
            ))
          ) : (
            <div className="health-row">
              <span className="pending" />
              <strong>Provider checks</strong>
              <small>Unavailable</small>
            </div>
          )}
        </section>
        <section className="admin-panel recent-events">
          <div className="panel-title">
            <div>
              <h2>Recent events</h2>
              <p>Latest backend event records</p>
            </div>
          </div>
          <div className="admin-table">
            <div className="table-head">
              <span>Event</span>
              <span>Studio</span>
              <span>Date</span>
              <span>Status</span>
            </div>
            {(data.events || []).map((event) => (
              <div className="table-row" key={event.id}>
                <span>
                  <strong>{event.name}</strong>
                  <small>{event.venue?.city || "—"}</small>
                </span>
                <span>{event.studio?.name || "—"}</span>
                <span>{formatDate(event.startsAt)}</span>
                <span>
                  <i
                    className={`status ${event.status === "live" ? "status-live" : "status-draft"}`}
                  >
                    {event.status}
                  </i>
                </span>
              </div>
            ))}
          </div>
        </section>
        <section className="admin-panel attention">
          <div className="panel-title">
            <div>
              <h2>Needs attention</h2>
              <p>Items requiring review</p>
            </div>
            <button onClick={() => setView("inquiries")}>
              Review requests
            </button>
          </div>
          <div className="attention-number">
            {String(counts.openInquiries || 0).padStart(2, "0")}
            <small>client requests awaiting review</small>
          </div>
          <div className="attention-row">
            <AlertTriangle size={18} />
            <span>
              <strong>Moderation and retention queues</strong>
              <small>
                {cases.length} moderation items ·{" "}
                {data.retention?.pagination?.total || 0} tracked photos
              </small>
            </span>
          </div>
        </section>
      </div>
    </>
  );
}

const EMPTY_EVENT_TYPE = {
  code: "",
  name: "",
  description: "",
  iconKey: "calendar",
  defaultDurationHours: "8",
  recommendedSpecialties: "",
  sortOrder: "100",
  status: "active",
};

function eventTypeSort(left, right) {
  return (
    Number(left.sortOrder || 0) - Number(right.sortOrder || 0) ||
    left.name.localeCompare(right.name)
  );
}

export function EventTypeManagement({
  eventTypes = [],
  create = async () => false,
  update = async () => false,
  archive = async () => false,
  busyId = "",
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [editor, setEditor] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const normalizedQuery = query.trim().toLowerCase();
  const visible = [...eventTypes].sort(eventTypeSort).filter((item) => {
    const matchesStatus = status === "all" || item.status === status;
    const haystack = [
      item.name,
      item.code,
      item.description,
      ...(item.recommendedSpecialtyCodes || []),
    ]
      .join(" ")
      .toLowerCase();
    return (
      matchesStatus && (!normalizedQuery || haystack.includes(normalizedQuery))
    );
  });

  function startCreate() {
    setEditor({ item: null, values: { ...EMPTY_EVENT_TYPE } });
  }

  function startEdit(item) {
    setEditor({
      item,
      values: {
        code: item.code,
        name: item.name,
        description: item.description || "",
        iconKey: item.iconKey || "calendar",
        defaultDurationHours: String(item.defaultDurationHours || 8),
        recommendedSpecialties: (item.recommendedSpecialtyCodes || []).join(
          ", ",
        ),
        sortOrder: String(item.sortOrder || 0),
        status: item.status,
      },
    });
  }

  function setField(field, value) {
    setEditor((current) => ({
      ...current,
      values: { ...current.values, [field]: value },
    }));
  }

  function payload(values, editing) {
    return {
      ...(!editing
        ? {
            code: values.code
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "_")
              .replace(/^_+|_+$/g, ""),
          }
        : {}),
      name: values.name.trim(),
      description: values.description.trim(),
      iconKey: values.iconKey.trim(),
      defaultDurationHours: Number(values.defaultDurationHours),
      recommendedSpecialtyCodes: values.recommendedSpecialties
        .split(",")
        .map((value) =>
          value
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, ""),
        )
        .filter(Boolean),
      sortOrder: Number(values.sortOrder),
      status: values.status,
    };
  }

  async function submit(event) {
    event.preventDefault();
    const succeeded = editor.item
      ? await update(editor.item, payload(editor.values, true))
      : await create(payload(editor.values, false));
    if (succeeded) setEditor(null);
  }

  async function toggleAvailability(item) {
    await update(item, {
      status: item.status === "active" ? "inactive" : "active",
    });
  }

  async function confirmArchive() {
    if (await archive(archiveTarget)) setArchiveTarget(null);
  }

  return (
    <>
      <Heading
        eyebrow="Catalogue governance"
        title="Event type catalogue"
        text="Control the categories available to event operations while preserving retired types for historical records."
        action={
          <button className="button button-dark" onClick={startCreate}>
            <Plus size={16} /> Add event type
          </button>
        }
      />
      <div className="partner-summary event-type-summary">
        <article>
          <small>Active categories</small>
          <strong>
            {eventTypes.filter((item) => item.status === "active").length}
          </strong>
        </article>
        <article>
          <small>Temporarily unavailable</small>
          <strong>
            {eventTypes.filter((item) => item.status === "inactive").length}
          </strong>
        </article>
        <article>
          <small>Archived</small>
          <strong>
            {eventTypes.filter((item) => item.status === "archived").length}
          </strong>
        </article>
      </div>
      <section className="admin-panel event-type-catalogue">
        <div className="catalogue-toolbar">
          <label>
            <Search size={16} />
            <input
              aria-label="Search event types"
              placeholder="Search name, code, or specialty"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <select
            aria-label="Filter event types by status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="event-type-list">
          {visible.map((item) => {
            const busy = busyId === item.id;
            return (
              <article className="event-type-row" key={item.id}>
                <div className="event-type-icon">
                  <CalendarDays size={19} />
                </div>
                <div className="event-type-copy">
                  <div>
                    <h2>{item.name}</h2>
                    <code>{item.code}</code>
                    <span className={`catalogue-status ${item.status}`}>
                      {item.status}
                    </span>
                  </div>
                  <p>
                    {item.description || "No catalogue description supplied."}
                  </p>
                  <small>
                    {item.defaultDurationHours}h default
                    <b aria-hidden="true">&middot;</b>
                    {(item.recommendedSpecialtyCodes || []).length
                      ? item.recommendedSpecialtyCodes
                          .map((value) => value.replaceAll("_", " "))
                          .join(", ")
                      : "No specialty recommendation"}
                  </small>
                </div>
                <div className="event-type-actions">
                  {item.status !== "archived" && (
                    <>
                      <button
                        className="button button-light"
                        disabled={busy}
                        onClick={() => startEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        className="button button-light"
                        disabled={busy}
                        onClick={() => toggleAvailability(item)}
                      >
                        {item.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="catalogue-archive"
                        aria-label={`Archive ${item.name}`}
                        disabled={busy}
                        onClick={() => setArchiveTarget(item)}
                      >
                        <Archive size={16} />
                      </button>
                    </>
                  )}
                </div>
              </article>
            );
          })}
          {!visible.length && (
            <div className="empty-state">No event types match this filter.</div>
          )}
        </div>
      </section>
      {editor && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <form className="catalogue-editor" onSubmit={submit}>
            <button
              type="button"
              className="qr-close"
              aria-label="Close event type editor"
              onClick={() => setEditor(null)}
            >
              <X size={20} />
            </button>
            <p className="eyebrow">Event catalogue</p>
            <h2>{editor.item ? "Edit event type" : "Create event type"}</h2>
            <p>
              Codes remain stable after creation so future event records can
              reference this category safely.
            </p>
            <div className="catalogue-form-grid">
              <label>
                Display name
                <input
                  required
                  minLength="2"
                  maxLength="160"
                  value={editor.values.name}
                  onChange={(event) => setField("name", event.target.value)}
                />
              </label>
              <label>
                Stable code
                <input
                  required
                  minLength="2"
                  maxLength="80"
                  disabled={Boolean(editor.item)}
                  placeholder="award_ceremony"
                  value={editor.values.code}
                  onChange={(event) => setField("code", event.target.value)}
                />
              </label>
              <label className="wide">
                Description
                <textarea
                  maxLength="1000"
                  value={editor.values.description}
                  onChange={(event) =>
                    setField("description", event.target.value)
                  }
                />
              </label>
              <label>
                Icon key
                <input
                  maxLength="80"
                  value={editor.values.iconKey}
                  onChange={(event) => setField("iconKey", event.target.value)}
                />
              </label>
              <label>
                Default duration (hours)
                <input
                  type="number"
                  min="1"
                  max="720"
                  required
                  value={editor.values.defaultDurationHours}
                  onChange={(event) =>
                    setField("defaultDurationHours", event.target.value)
                  }
                />
              </label>
              <label className="wide">
                Recommended specialties
                <input
                  placeholder="wedding, candid, videography"
                  value={editor.values.recommendedSpecialties}
                  onChange={(event) =>
                    setField("recommendedSpecialties", event.target.value)
                  }
                />
              </label>
              <label>
                Sort order
                <input
                  type="number"
                  min="0"
                  max="10000"
                  required
                  value={editor.values.sortOrder}
                  onChange={(event) =>
                    setField("sortOrder", event.target.value)
                  }
                />
              </label>
              <label>
                Availability
                <select
                  value={editor.values.status}
                  onChange={(event) => setField("status", event.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button button-light"
                onClick={() => setEditor(null)}
              >
                Cancel
              </button>
              <button
                className="button button-dark"
                disabled={busyId === (editor.item?.id || "new")}
              >
                {busyId === (editor.item?.id || "new") && (
                  <LoaderCircle className="spin" size={16} />
                )}
                {editor.item ? "Save changes" : "Create category"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {archiveTarget && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <section className="catalogue-confirm">
            <span>
              <Archive size={22} />
            </span>
            <h2>Archive {archiveTarget.name}?</h2>
            <p>
              It will disappear from new-event choices, while existing event
              history remains intact. Archiving is final.
            </p>
            <div>
              <button
                className="button button-light"
                onClick={() => setArchiveTarget(null)}
              >
                Keep event type
              </button>
              <button
                className="button partner-danger"
                disabled={busyId === archiveTarget.id}
                onClick={confirmArchive}
              >
                Archive permanently
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export function PhotographerApprovals({
  profiles,
  review,
  remove = async () => false,
  busyId = "",
  createPhotographer,
  studios = [],
  eventTypes = [],
}) {
  const [notes, setNotes] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    displayName: "",
    email: "",
    phone: "",
    businessName: "",
    headline: "Professional Event Photographer",
    primaryCity: "",
    experienceYears: 3,
    serviceRadiusKm: 50,
    specialtyCodes: ["wedding"],
    studioId: "",
    minPrice: 5000,
    maxPrice: 50000,
    bio: "Verified professional photographer onboarded by studio administrator.",
  });

  const pending = profiles.filter((profile) =>
    ["submitted", "under_review"].includes(profile.status),
  );

  function noteFor(profile) {
    return notes[profile.id] || "";
  }

  function act(profile, status) {
    const note = noteFor(profile).trim();
    if (["rejected", "suspended"].includes(status) && note.length < 3) return;
    review(profile, status, note || undefined);
  }

  const availableSpecialties = eventTypes?.length
    ? eventTypes
        .filter((et) => et.status === "active")
        .map((et) => ({ code: et.code, label: et.name }))
    : [
        { code: "wedding", label: "Wedding" },
        { code: "pre_wedding", label: "Pre-Wedding" },
        { code: "engagement", label: "Engagement" },
        { code: "portrait", label: "Portrait" },
        { code: "candid", label: "Candid" },
        { code: "corporate", label: "Corporate" },
        { code: "birthday", label: "Birthday" },
      ];

  function toggleSpecialty(code) {
    setForm((current) => {
      const exists = current.specialtyCodes.includes(code);
      const updated = exists
        ? current.specialtyCodes.filter((c) => c !== code)
        : [...current.specialtyCodes, code];
      return { ...current, specialtyCodes: updated };
    });
  }

  async function handleAddSubmit(event) {
    event.preventDefault();
    if (!createPhotographer) return;
    setAddBusy(true);
    setAddError("");
    try {
      const ok = await createPhotographer({
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        phone: form.phone.trim() || undefined,
        businessName: form.businessName.trim() || undefined,
        headline: form.headline.trim() || undefined,
        bio: form.bio.trim() || undefined,
        primaryCity: form.primaryCity.trim(),
        experienceYears: Number(form.experienceYears) || 1,
        serviceRadiusKm: Number(form.serviceRadiusKm) || 50,
        specialtyCodes: form.specialtyCodes,
        studioId: form.studioId || undefined,
        pricingRange: {
          minimum: Number(form.minPrice) || 5000,
          maximum: Number(form.maxPrice) || 50000,
          currency: "INR",
        },
      });
      if (ok) {
        setAddOpen(false);
        setForm({
          displayName: "",
          email: "",
          phone: "",
          businessName: "",
          headline: "Professional Event Photographer",
          primaryCity: "",
          experienceYears: 3,
          serviceRadiusKm: 50,
          specialtyCodes: ["wedding"],
          studioId: "",
          minPrice: 5000,
          maxPrice: 50000,
          bio: "Verified professional photographer onboarded by studio administrator.",
        });
      }
    } catch (err) {
      setAddError(err.message || "Failed to add photographer");
    } finally {
      setAddBusy(false);
    }
  }

  return (
    <>
      <Heading
        eyebrow="Partner network"
        title="Photographer approvals"
        text="Review submitted profiles before photographers can become PhotoX partners, or directly onboard verified partners."
        action={
          createPhotographer ? (
            <button
              className="button button-dark"
              onClick={() => setAddOpen(true)}
            >
              <Plus size={16} /> Add photographer
            </button>
          ) : undefined
        }
      />
      <div className="partner-summary">
        <article>
          <small>Awaiting decision</small>
          <strong>{pending.length}</strong>
        </article>
        <article>
          <small>Approved partners</small>
          <strong>
            {profiles.filter((profile) => profile.status === "approved").length}
          </strong>
        </article>
        <article>
          <small>Total applications</small>
          <strong>{profiles.length}</strong>
        </article>
      </div>
      {profiles.length ? (
        <div className="partner-grid">
          {profiles.map((profile) => {
            const applicant = profile.user || {};
            const note = noteFor(profile);
            const busy = busyId === profile.id;
            return (
              <article className="partner-card admin-panel" key={profile.id}>
                <header>
                  <div className="partner-avatar">
                    {(applicant.displayName || applicant.email || "P")
                      .split(/\s+/)
                      .map((part) => part[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                  <div>
                    <p className="eyebrow">
                      {profile.businessName || "Independent partner"}
                    </p>
                    <h2>{applicant.displayName || applicant.email}</h2>
                    <p>
                      {applicant.email}
                      {applicant.phone ? ` · ${applicant.phone}` : ""}
                    </p>
                  </div>
                  <span className={`partner-status partner-${profile.status}`}>
                    {profile.status.replace("_", " ")}
                  </span>
                </header>
                <div className="partner-details">
                  <span>
                    <small>Location</small>
                    <strong>{profile.primaryCity || "Not supplied"}</strong>
                  </span>
                  <span>
                    <small>Service Radius</small>
                    <strong>
                      {profile.serviceRadiusKm !== undefined
                        ? `${profile.serviceRadiusKm} km`
                        : "Not supplied"}
                    </strong>
                  </span>
                  <span>
                    <small>Experience</small>
                    <strong>
                      {profile.experienceYears === undefined
                        ? "Not supplied"
                        : `${profile.experienceYears} years`}
                    </strong>
                  </span>
                  <span>
                    <small>Pricing Range</small>
                    <strong>
                      {profile.pricingRange?.minimum !== undefined &&
                      profile.pricingRange?.maximum !== undefined
                        ? `${profile.pricingRange.currency || "INR"} ${profile.pricingRange.minimum.toLocaleString()} – ${profile.pricingRange.maximum.toLocaleString()}`
                        : "Not supplied"}
                    </strong>
                  </span>
                  <span>
                    <small>Languages</small>
                    <strong>
                      {(profile.languages || []).length
                        ? profile.languages.join(", ")
                        : "Not supplied"}
                    </strong>
                  </span>
                  <span>
                    <small>Submitted</small>
                    <strong>{formatDate(profile.submittedAt)}</strong>
                  </span>
                </div>
                {(profile.equipment || []).length > 0 && (
                  <div className="partner-equipment">
                    <small>Equipment: </small>
                    <span>{profile.equipment.join(", ")}</span>
                  </div>
                )}
                <div className="partner-specialties">
                  {(profile.specialtyCodes || []).length ? (
                    profile.specialtyCodes.map((specialty) => (
                      <span key={specialty}>
                        {specialty.replaceAll("_", " ")}
                      </span>
                    ))
                  ) : (
                    <small>No specialties supplied</small>
                  )}
                </div>
                {profile.headline && (
                  <p className="partner-headline">
                    <strong>Headline: </strong>
                    {profile.headline}
                  </p>
                )}
                {profile.bio && (
                  <div className="partner-bio">
                    <small>Professional Biography</small>
                    <p>{profile.bio}</p>
                  </div>
                )}
                {(profile.portfolioUrls || []).length > 0 && (
                  <div className="partner-portfolios">
                    <small>Portfolio & Work Samples</small>
                    <div className="partner-portfolio-links">
                      {profile.portfolioUrls.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="partner-link-pill"
                        >
                          <ExternalLink size={12} />
                          <span>{url}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {profile.reviewNote && (
                  <div className="partner-previous-note">
                    <small>Latest Admin Note</small>
                    <p>{profile.reviewNote}</p>
                  </div>
                )}
                <label className="partner-note">
                  Review note
                  <textarea
                    value={note}
                    maxLength={2000}
                    placeholder="Required when rejecting or suspending a partner"
                    onChange={(event) =>
                      setNotes((current) => ({
                        ...current,
                        [profile.id]: event.target.value,
                      }))
                    }
                  />
                </label>
                <footer className="partner-actions">
                  {profile.status === "submitted" && (
                    <button
                      className="button button-light"
                      disabled={busy}
                      onClick={() => act(profile, "under_review")}
                    >
                      Start review
                    </button>
                  )}
                  {[
                    "submitted",
                    "under_review",
                    "rejected",
                    "suspended",
                  ].includes(profile.status) && (
                    <button
                      className="button button-dark"
                      disabled={busy}
                      onClick={() => act(profile, "approved")}
                    >
                      <Check size={15} /> Approve partner
                    </button>
                  )}
                  {["submitted", "under_review"].includes(profile.status) && (
                    <button
                      className="button partner-danger"
                      disabled={busy || note.trim().length < 3}
                      onClick={() => act(profile, "rejected")}
                    >
                      Reject
                    </button>
                  )}
                  {profile.status === "approved" && (
                    <button
                      className="button partner-danger"
                      disabled={busy || note.trim().length < 3}
                      onClick={() => act(profile, "suspended")}
                    >
                      Suspend partner
                    </button>
                  )}
                  <button
                    className="button partner-danger"
                    disabled={busy}
                    onClick={() => setDeleteTarget(profile)}
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="admin-panel empty-state">
          No Photographer applications have been submitted yet.
        </div>
      )}
      {addOpen && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <form className="catalogue-editor" onSubmit={handleAddSubmit}>
            <button
              type="button"
              className="qr-close"
              aria-label="Close photographer onboarding"
              onClick={() => setAddOpen(false)}
            >
              <X size={20} />
            </button>
            <p className="eyebrow">Partner onboarding</p>
            <h2>Add & approve photographer</h2>
            <p>
              Direct admin onboarding automatically verifies and approves this
              photographer profile, making them immediately active and eligible
              for event assignments.
            </p>
            <div
              style={{
                background: "#eef7f3",
                border: "1px solid #cce8dc",
                borderRadius: "12px",
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                margin: "16px 0",
                color: "#186644",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              <ShieldCheck size={20} style={{ flexShrink: 0 }} />
              <span>
                <strong>Auto-Approved Partner:</strong> As this profile is added
                directly by the admin, it is automatically marked as Approved
                and added to the studio directory.
              </span>
            </div>
            {addError && <div className="admin-alert">{addError}</div>}
            <div className="catalogue-form-grid">
              <label>
                Full name *
                <input
                  required
                  minLength="2"
                  maxLength="120"
                  placeholder="e.g. Yash Parmar"
                  value={form.displayName}
                  onChange={(e) =>
                    setForm({ ...form, displayName: e.target.value })
                  }
                />
              </label>
              <label>
                Email address *
                <input
                  required
                  type="email"
                  placeholder="photographer@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </label>
              <label>
                Phone number
                <input
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </label>
              <label>
                Business / Brand name
                <input
                  placeholder="e.g. Yash Moments & Lens"
                  value={form.businessName}
                  onChange={(e) =>
                    setForm({ ...form, businessName: e.target.value })
                  }
                />
              </label>
              <label>
                Primary city *
                <input
                  required
                  placeholder="e.g. Ahmedabad, Mumbai, Surat"
                  value={form.primaryCity}
                  onChange={(e) =>
                    setForm({ ...form, primaryCity: e.target.value })
                  }
                />
              </label>
              <label>
                Experience (Years)
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={form.experienceYears}
                  onChange={(e) =>
                    setForm({ ...form, experienceYears: e.target.value })
                  }
                />
              </label>
              <label>
                Service radius (KM)
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={form.serviceRadiusKm}
                  onChange={(e) =>
                    setForm({ ...form, serviceRadiusKm: e.target.value })
                  }
                />
              </label>
              {studios.length > 0 && (
                <label>
                  Studio association
                  <select
                    value={form.studioId}
                    onChange={(e) =>
                      setForm({ ...form, studioId: e.target.value })
                    }
                  >
                    <option value="">Independent (no studio)</option>
                    {studios.map((studio) => (
                      <option key={studio.id} value={studio.id}>
                        {studio.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="wide">
                Specialties
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  {availableSpecialties.map((item) => {
                    const selected = form.specialtyCodes.includes(item.code);
                    return (
                      <button
                        type="button"
                        key={item.code}
                        onClick={() => toggleSpecialty(item.code)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: 700,
                          cursor: "pointer",
                          border: selected
                            ? "1px solid #162441"
                            : "1px solid #dfe6f1",
                          background: selected ? "#162441" : "#ffffff",
                          color: selected ? "#ffffff" : "#4a5568",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {selected ? "✓ " : "+ "}
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </label>
              <label>
                Min price per event (INR)
                <input
                  type="number"
                  min="0"
                  step="500"
                  value={form.minPrice}
                  onChange={(e) =>
                    setForm({ ...form, minPrice: e.target.value })
                  }
                />
              </label>
              <label>
                Max price per event (INR)
                <input
                  type="number"
                  min="0"
                  step="500"
                  value={form.maxPrice}
                  onChange={(e) =>
                    setForm({ ...form, maxPrice: e.target.value })
                  }
                />
              </label>
              <label className="wide">
                Headline
                <input
                  maxLength="180"
                  placeholder="e.g. Award-winning Candid Wedding & Pre-wedding specialist"
                  value={form.headline}
                  onChange={(e) =>
                    setForm({ ...form, headline: e.target.value })
                  }
                />
              </label>
              <label className="wide">
                Bio / Notes
                <textarea
                  maxLength="2000"
                  rows="3"
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button button-light"
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button-dark"
                disabled={
                  addBusy ||
                  !form.displayName ||
                  !form.email ||
                  !form.primaryCity
                }
              >
                {addBusy ? (
                  <LoaderCircle className="spin" size={16} />
                ) : (
                  <Check size={16} />
                )}
                Add & approve partner
              </button>
            </footer>
          </form>
        </div>
      )}
      {deleteTarget && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <section className="catalogue-confirm">
            <span>
              <Trash2 size={22} />
            </span>
            <h2>
              Delete {deleteTarget.user?.displayName || "photographer"}?
            </h2>
            <p>
              This permanently removes the photographer profile, assignments,
              studio memberships and booking test records. The email account is
              preserved so the same details can be onboarded again.
            </p>
            <div>
              <button
                className="button button-light"
                onClick={() => setDeleteTarget(null)}
              >
                Keep photographer
              </button>
              <button
                className="button partner-danger"
                disabled={busyId === deleteTarget.id}
                onClick={async () => {
                  if (await remove(deleteTarget)) setDeleteTarget(null);
                }}
              >
                {busyId === deleteTarget.id ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export function LegacyEventManagement({
  events,
  studios,
  subscriptions,
  token,
  create,
  publish,
  showQr,
  setToast,
}) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    studioId: "",
    name: "",
    clientEmail: "",
    clientDisplayName: "",
    photographerId: "",
    startsAt: "",
    endsAt: "",
    venueName: "",
    venueAddress: "",
    venueCity: "",
  });
  const paidEventIds = new Set(
    (subscriptions?.subscriptions || [])
      .filter(
        (item) =>
          ["active", "trialing"].includes(item.status) && item.event?.id,
      )
      .map((item) => item.event.id),
  );

  async function chooseStudio(studioId) {
    setForm((current) => ({ ...current, studioId, photographerId: "" }));
    if (!studioId) {
      setMembers([]);
      return;
    }
    try {
      setMembers(
        (await loadStudioMembers(token, studioId)).filter(
          (item) =>
            item.status === "active" &&
            ["owner", "photographer"].includes(item.role),
        ),
      );
    } catch (requestError) {
      setToast(requestError.message || "Unable to load studio members");
    }
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    const values = {
      studioId: form.studioId,
      name: form.name,
      clientEmail: form.clientEmail,
      ...(form.clientDisplayName
        ? { clientDisplayName: form.clientDisplayName }
        : {}),
      assignedPhotographerIds: [form.photographerId],
      startsAt: new Date(form.startsAt).toISOString(),
      ...(form.endsAt ? { endsAt: new Date(form.endsAt).toISOString() } : {}),
      venue: {
        name: form.venueName,
        address: form.venueAddress,
        city: form.venueCity,
      },
    };
    const created = await create(values);
    setBusy(false);
    if (created) {
      setOpen(false);
      setForm({
        studioId: "",
        name: "",
        clientEmail: "",
        clientDisplayName: "",
        photographerId: "",
        startsAt: "",
        endsAt: "",
        venueName: "",
        venueAddress: "",
        venueCity: "",
      });
    }
  }

  return (
    <>
      <Heading
        eyebrow="Event provisioning"
        title="Events & QR access"
        text="Create the client's draft, wait for plan activation, then publish and share the QR."
        action={
          <button
            className="button button-dark"
            onClick={() => setOpen((value) => !value)}
          >
            <Plus size={16} /> New event
          </button>
        }
      />
      <div className="workflow-strip">
        <span>
          <b>1</b>Admin creates draft
        </span>
        <span>
          <b>2</b>Client activates plan
        </span>
        <span>
          <b>3</b>Admin publishes
        </span>
        <span>
          <b>4</b>Guests scan & sign in
        </span>
      </div>
      {open && (
        <form className="event-form admin-panel" onSubmit={submit}>
          <div className="panel-title">
            <div>
              <h2>Prepare a client event</h2>
              <p>The event stays private until its subscription is active.</p>
            </div>
            <button type="button" onClick={() => setOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="event-form-grid">
            <label>
              Event name
              <input
                required
                value={form.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
              />
            </label>
            <label>
              Studio
              <select
                required
                value={form.studioId}
                onChange={(event) => chooseStudio(event.target.value)}
              >
                <option value="">Choose studio</option>
                {(studios || []).map((studio) => (
                  <option value={studio.id} key={studio.id}>
                    {studio.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Client name
              <input
                value={form.clientDisplayName}
                onChange={(event) =>
                  setForm({ ...form, clientDisplayName: event.target.value })
                }
              />
            </label>
            <label>
              Client email
              <input
                required
                type="email"
                value={form.clientEmail}
                onChange={(event) =>
                  setForm({ ...form, clientEmail: event.target.value })
                }
              />
            </label>
            <label>
              Photographer
              <select
                required
                value={form.photographerId}
                onChange={(event) =>
                  setForm({ ...form, photographerId: event.target.value })
                }
              >
                <option value="">Choose photographer</option>
                {members.map((member) => (
                  <option value={member.user?.id} key={member.id}>
                    {member.user?.displayName || member.user?.email} ·{" "}
                    {member.role}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Starts at
              <input
                required
                type="datetime-local"
                value={form.startsAt}
                onChange={(event) =>
                  setForm({ ...form, startsAt: event.target.value })
                }
              />
            </label>
            <label>
              Ends at
              <input
                type="datetime-local"
                value={form.endsAt}
                onChange={(event) =>
                  setForm({ ...form, endsAt: event.target.value })
                }
              />
            </label>
            <label>
              Venue
              <input
                value={form.venueName}
                onChange={(event) =>
                  setForm({ ...form, venueName: event.target.value })
                }
              />
            </label>
            <label>
              City
              <input
                value={form.venueCity}
                onChange={(event) =>
                  setForm({ ...form, venueCity: event.target.value })
                }
              />
            </label>
            <label className="wide">
              Address
              <input
                value={form.venueAddress}
                onChange={(event) =>
                  setForm({ ...form, venueAddress: event.target.value })
                }
              />
            </label>
          </div>
          <button disabled={busy} className="button button-dark">
            {busy ? (
              <LoaderCircle className="spin" size={17} />
            ) : (
              <Plus size={17} />
            )}{" "}
            Create private draft
          </button>
        </form>
      )}
      <section className="admin-panel event-table">
        <div className="panel-title">
          <div>
            <h2>Provisioned events</h2>
            <p>{events?.length || 0} events across the platform</p>
          </div>
        </div>
        <div className="table-head">
          <span>Event</span>
          <span>Client</span>
          <span>Plan</span>
          <span>Status</span>
          <span>Action</span>
        </div>
        {events?.length ? (
          events.map((item) => {
            const paid = paidEventIds.has(item.id);
            return (
              <div className="table-row" key={item.id}>
                <span>
                  <strong>{item.name}</strong>
                  <small>
                    {item.studio?.name || "Unassigned"} ·{" "}
                    {formatDate(item.startsAt)}
                  </small>
                </span>
                <span>
                  {item.client?.displayName || item.client?.email || "—"}
                </span>
                <span>
                  <i
                    className={`status ${paid ? "status-paid" : "status-review"}`}
                  >
                    {item.billingRequired
                      ? paid
                        ? "active"
                        : "awaiting payment"
                      : "not required"}
                  </i>
                </span>
                <span>
                  <i
                    className={`status ${["live", "scheduled", "delivered"].includes(item.status) ? "status-live" : "status-draft"}`}
                  >
                    {item.status}
                  </i>
                </span>
                <span className="event-actions">
                  {item.status === "draft" ? (
                    <button
                      disabled={item.billingRequired && !paid}
                      onClick={() => publish(item)}
                      className="button button-dark"
                    >
                      <Send size={15} /> Publish
                    </button>
                  ) : (
                    <button
                      onClick={() => showQr(item)}
                      className="button button-light"
                    >
                      <QrCode size={15} /> QR code
                    </button>
                  )}
                </span>
              </div>
            );
          })
        ) : (
          <div className="empty-row">No events have been created.</div>
        )}
      </section>
    </>
  );
}

function QrModal({ access, close, setToast }) {
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(access.entryUrl);
      setToast("Guest link copied");
    } catch {
      setToast("Copy failed. Select the link manually.");
    }
  }
  return (
    <div className="admin-modal" role="dialog" aria-modal="true">
      <section className="qr-card">
        <button className="qr-close" onClick={close} aria-label="Close">
          <X />
        </button>
        <p className="eyebrow">Guest access</p>
        <h2>{access.event?.name}</h2>
        <p>
          Guests scan this code, sign in or create an account, and are joined
          only to this event.
        </p>
        <img src={access.qrDataUrl} alt={`QR code for ${access.event?.name}`} />
        <input
          readOnly
          value={access.entryUrl || ""}
          aria-label="Guest event link"
        />
        <div>
          <button className="button button-dark" onClick={copyLink}>
            <Copy size={16} /> Copy link
          </button>
          <a
            className="button button-light"
            href={access.qrDataUrl}
            download={`${access.event?.slug || "event"}-qr.png`}
          >
            <QrCode size={16} /> Download QR
          </a>
        </div>
        <small>
          {access.startsAt ? `Starts ${formatDate(access.startsAt)} · ` : ""}
          Expires {formatDate(access.expiresAt)}
        </small>
      </section>
    </div>
  );
}

function Moderation({ cases, approve, remove }) {
  return (
    <>
      <Heading
        eyebrow="Trust & safety"
        title="Moderation queue"
        text="Live privacy and content reports."
        action={
          <span className="button button-dark">
            <Flag size={16} /> {cases.length} open
          </span>
        }
      />
      {cases.length ? (
        <div className="moderation-grid">
          {cases.map((item) => (
            <article key={item.id}>
              <div className="moderation-media-empty">
                <Image size={32} />
              </div>
              <div className="case-body">
                <span className="status status-review">
                  {item.status || "open"}
                </span>
                <h3>{humanize(item.reason || "Review requested")}</h3>
                <p>{item.event?.name || "Unknown event"}</p>
                <dl>
                  <div>
                    <dt>Reported by</dt>
                    <dd>{item.reportedBy?.displayName || "Account user"}</dd>
                  </div>
                  <div>
                    <dt>Received</dt>
                    <dd>{formatDate(item.createdAt)}</dd>
                  </div>
                </dl>
                <div className="case-actions">
                  <button className="button button-light">
                    <Eye size={16} /> Inspect
                  </button>
                  <button
                    className="approve"
                    aria-label="Approve case"
                    onClick={() => approve(item)}
                  >
                    <Check size={17} />
                  </button>
                  <button
                    className="remove"
                    aria-label="Remove content"
                    onClick={() => remove(item)}
                  >
                    <Trash2 size={17} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <span>
            <Check size={26} />
          </span>
          <h2>Queue cleared</h2>
          <p>There are no open moderation items.</p>
        </div>
      )}
    </>
  );
}

function Retention({ retention, overview, setToast }) {
  const photos = retention?.photos || [];
  const warningCount = photos.filter(
    (photo) =>
      photo.retention?.warningAt &&
      new Date(photo.retention.warningAt) <= new Date(),
  ).length;
  const expiryCount = photos.filter(
    (photo) =>
      photo.retention?.expiresAt &&
      new Date(photo.retention.expiresAt) <= new Date(),
  ).length;
  return (
    <>
      <Heading
        eyebrow="Storage lifecycle"
        title="Retention control"
        text="Backend-managed photo retention schedules."
        action={
          <button
            className="button button-light"
            onClick={() => setToast("Retention export is not enabled yet")}
          >
            <HardDrive size={16} /> Export
          </button>
        }
      />
      <div className="retention-summary">
        <article>
          <span>
            <HardDrive />
          </span>
          <div>
            <small>Total storage</small>
            <strong>{formatBytes(overview?.storageBytes || 0)}</strong>
            <em>Persisted media metadata</em>
          </div>
        </article>
        <article>
          <span>
            <Clock3 />
          </span>
          <div>
            <small>Warning due</small>
            <strong>{warningCount}</strong>
            <em>Visible page</em>
          </div>
        </article>
        <article>
          <span>
            <Trash2 />
          </span>
          <div>
            <small>Expiry due</small>
            <strong>{expiryCount}</strong>
            <em>Visible page</em>
          </div>
        </article>
      </div>
      <section className="admin-panel retention-table">
        <div className="panel-title">
          <div>
            <h2>Photo lifecycle</h2>
            <p>{retention?.pagination?.total || 0} tracked records</p>
          </div>
        </div>
        <div className="table-head">
          <span>Photo</span>
          <span>Event</span>
          <span>Warning</span>
          <span>Expiry</span>
          <span />
        </div>
        {photos.length ? (
          photos.map((photo) => (
            <div className="table-row" key={photo.id}>
              <span>
                <strong>{photo.originalFilename}</strong>
                <small>{photo.studio?.name || "—"}</small>
              </span>
              <span>{photo.event?.name || "—"}</span>
              <span>{formatDate(photo.retention?.warningAt)}</span>
              <span>
                <i className="status status-review">
                  {formatDate(photo.retention?.expiresAt)}
                </i>
              </span>
              <button aria-label="Photo options">
                <MoreHorizontal />
              </button>
            </div>
          ))
        ) : (
          <div className="empty-row">No retention records returned.</div>
        )}
      </section>
    </>
  );
}

function Subscriptions({ report, plans = [], setToast }) {
  const summary = report?.summary || { total: 0, byStatus: {}, byPlan: {} };
  const subscriptions = report?.subscriptions || [];
  const active = summary.byStatus?.active || 0;
  const attention =
    (summary.byStatus?.past_due || 0) + (summary.byStatus?.unpaid || 0);
  return (
    <>
      <Heading
        eyebrow="Membership operations"
        title="Subscriptions"
        text="Backend subscription and entitlement records."
      />
      <div className="subscription-cards">
        <article>
          <small>Total subscriptions</small>
          <strong>{summary.total || 0}</strong>
          <em>All plans</em>
        </article>
        <article>
          <small>Active subscribers</small>
          <strong>{active}</strong>
          <em>Current records</em>
        </article>
        <article>
          <small>Needs attention</small>
          <strong>{attention}</strong>
          <em>Past due or unpaid</em>
        </article>
        <article>
          <small>Marketplace split</small>
          <strong>85 / 15</strong>
          <em>Photographer / PhotoX</em>
        </article>
      </div>
      <section className="admin-panel subscriptions-table">
        <div className="panel-title">
          <div>
            <h2>Live client plan catalogue</h2>
            <p>
              Real checkout data from the billing API, including storage and
              enforced limits
            </p>
          </div>
        </div>
        <div className="table-head">
          <span>Plan</span>
          <span>Price</span>
          <span>Storage & photos</span>
          <span>Retention</span>
        </div>
        {plans.length ? (
          plans.map((plan) => (
            <div className="table-row" key={plan.code}>
              <span>
                <strong>{plan.name || plan.code}</strong>
                <small>{plan.code}</small>
              </span>
              <span>
                {Number.isInteger(plan.amountMinor)
                  ? new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: plan.currency || "INR",
                    }).format(plan.amountMinor / 100)
                  : "Configure Stripe price"}
              </span>
              <span>
                {formatBytes(
                  plan.entitlements?.storageBytes ?? plan.storageBytes,
                )}{" "}
                · {plan.entitlements?.maxPhotos?.toLocaleString() || "Flexible"}{" "}
                photos
              </span>
              <span>{plan.entitlements?.retentionDays || 90} days</span>
            </div>
          ))
        ) : (
          <div className="empty-row">
            No published client plans. Publish Low, Medium and High below.
          </div>
        )}
        <div className="empty-row" style={{ background: "#f8fafc", padding: "1.2rem", borderRadius: "10px", borderLeft: "4px solid #6366f1", textAlign: "left", marginTop: "1rem" }}>
          <strong style={{ display: "block", color: "#1e293b", fontSize: "1rem", marginBottom: "0.4rem" }}>
            PhotoX 85 / 15 Commission Policy
          </strong>
          <span style={{ color: "#475569", fontSize: "0.9rem", lineHeight: 1.5 }}>
            The client pays the event subscription package (100%). From this subscription amount, PhotoX retains <strong>15%</strong> as platform commission, and <strong>85%</strong> is allocated for the assigned photographer and disbursed through the Admin. No additional fees or photographer payments are charged to the client.
          </span>
        </div>
      </section>
      <section className="admin-panel subscriptions-table">
        <div className="panel-title">
          <div>
            <h2>Client event subscriptions & Photographer 85/15 Payouts</h2>
            <p>Track collected client subscriptions, 15% platform commission, and 85% photographer allocation</p>
          </div>
          <button onClick={() => setToast("CSV export is not enabled yet")}>
            Export CSV
          </button>
        </div>
        <div className="table-head" style={{ gridTemplateColumns: "1.4fr 0.8fr 1fr 1fr 1fr 1.2fr 0.8fr" }}>
          <span>Event / client</span>
          <span>Plan</span>
          <span>Subscription (100%)</span>
          <span>PhotoX (15%)</span>
          <span>Photographer (85%)</span>
          <span>Assigned Photographer</span>
          <span>Status</span>
        </div>
        {subscriptions.length ? (
          subscriptions.map((subscription) => {
            const comm = subscription.commission || {
              totalAmountMinor: subscription.commercialSnapshot?.amountMinor || 1500000,
              platformCommissionMinor: Math.round((subscription.commercialSnapshot?.amountMinor || 1500000) * 0.15),
              photographerShareMinor:
                (subscription.commercialSnapshot?.amountMinor || 1500000) -
                Math.round((subscription.commercialSnapshot?.amountMinor || 1500000) * 0.15),
              currency: "INR",
              photographers: []
            };
            const photographerName =
              comm.photographers?.[0]?.name ||
              (subscription.event?.assignedPhotographerIds?.[0]?.displayName) ||
              "Unassigned";
            const formatMoney = (minor, cur) =>
              new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: cur || "INR",
                maximumFractionDigits: 0
              }).format((minor || 0) / 100);

            return (
              <div className="table-row" key={subscription.id} style={{ gridTemplateColumns: "1.4fr 0.8fr 1fr 1fr 1fr 1.2fr 0.8fr" }}>
                <span>
                  <strong>
                    {subscription.event?.name ||
                      subscription.studio?.name ||
                      "Unassigned"}
                  </strong>
                  <small>{subscription.owner?.email || subscription.id}</small>
                </span>
                <span>{subscription.planCode}</span>
                <span>
                  <strong>{formatMoney(comm.totalAmountMinor, comm.currency)}</strong>
                </span>
                <span style={{ color: "#4f46e5", fontWeight: "600" }}>
                  {formatMoney(comm.platformCommissionMinor, comm.currency)}
                </span>
                <span style={{ color: "#16a34a", fontWeight: "bold" }}>
                  {formatMoney(comm.photographerShareMinor, comm.currency)}
                </span>
                <span>
                  <small style={{ fontWeight: 600 }}>{photographerName}</small>
                </span>
                <span>
                  <i
                    className={`status ${subscription.status === "active" ? "status-paid" : "status-error"}`}
                  >
                    {subscription.status}
                  </i>
                </span>
              </div>
            );
          })
        ) : (
          <div className="empty-row">No subscription records returned.</div>
        )}
      </section>
      <PlanCatalogManagement onNotice={setToast} />
    </>
  );
}

function Confirm({ cancel, confirm }) {
  return (
    <div className="confirm-backdrop" role="dialog" aria-modal="true">
      <section className="confirm">
        <button className="confirm-close" onClick={cancel}>
          <X />
        </button>
        <span className="danger-icon">
          <Trash2 />
        </span>
        <h2>Remove reported content?</h2>
        <p>
          This records a real moderation decision in the development database.
        </p>
        <div>
          <button className="button button-light" onClick={cancel}>
            Cancel
          </button>
          <button className="button danger-button" onClick={confirm}>
            Mark removed
          </button>
        </div>
      </section>
    </div>
  );
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}
function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = value;
  let index = -1;
  do {
    size /= 1024;
    index += 1;
  } while (size >= 1024 && index < units.length - 1);
  return `${size.toFixed(size >= 10 ? 1 : 2)} ${units[index]}`;
}
function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
}
function humanize(value) {
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default App;
